import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { getClassesQuery, useGetStudentsInClassesQuery } from '@pm2/api';
import { pm2SitemapStore, findPageDescriptorById } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { useModalContentManager } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { CopyLinkButton, Tooltip, Button } from '@ui/kit';
import { LinkIcon, ShareIcon } from '@ui/kit/icons';
import { asyncTimeout } from '@utils/common';
import { useToggleState, useLoaderState } from '@utils/react';
import { useShareRevisionMutation } from '../../api/mutation.share.js';
import { heading, copyLinkThrobber, shareText, buttonsWrapper } from '../_shared.style.js';
import { Trigger } from './atom.trigger.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { getAllRevisionsQuery } from '../../api/query.get-all.js';

function Root(props) {
  const queryClient = useQueryClient();
  const user = useUserIdentityState(state => state.user);
  const modalManager = useModalContentManager();
  const shareMutation = useShareRevisionMutation();
  const classes = useQuery(getClassesQuery.useQueryConfig());
  const students = useGetStudentsInClassesQuery(classes.data?.map(it => it.id) ?? []);
  const revisionId = useRevisionId(props);
  const basePath = pm2ConfigStore.useSelector(state => state.baseUrl);
  const courseDescriptor = pm2SitemapStore.useSelector(state => findPageDescriptorById(state, props.revision.courseId));
  const [isSubmitting, submitActions] = useToggleState(false);
  const loadingDataRef = useLoaderState(!classes.data, {
    delayMs: 1000,
    durationMs: 500
  });
  const classTriggerValue = props.selectedClasses?.map(it => it.name).sort((a, b) => a.localeCompare(b, "da-DK", {
    numeric: true
  })).join(", ");
  const studentsTriggerValue = (() => {
    const selectedStudentNames = props.selectedStudents?.map(it => it.name).sort((a, b) => a.localeCompare(b, "da-DK", {
      numeric: true
    }));
    if (selectedStudentNames.length > 0) {
      return selectedStudentNames.join(", ");
    }
    if (props.selectedClasses.length > 0) {
      return "Alle elever";
    }
    return undefined;
  })();
  const studentsDisabled = students.isLoading || props.selectedClasses.length === 0;
  const canShare = props.selectedClasses.length > 0;
  const handleShare = async () => {
    try {
      submitActions.enable();
      let revision;
      const startedAt = Date.now();

      // In case we've been passed a local revision that has not yet been
      // synced with the backend, then poll our data and wait for the revision
      // to have been successfully saved.
      while (!revision?.__syncedWithBackend) {
        const currentRevisions = queryClient.getQueryData(getAllRevisionsQuery.queryKey({
          user,
          courseId: props.revision.courseId
        }));
        const targetRevision = currentRevisions?.find(it => it.__clientId === props.revision.__clientId);
        if (targetRevision?.__syncedWithBackend) {
          revision = targetRevision;
          break;
        }
        if (Date.now() - startedAt > 5_000) {
          throw new Error("Unable to share revision that has not been synced with the backend.");
        }
        await asyncTimeout(100);
      }
      await shareMutation.mutateAsync({
        revision,
        teamIds: props.selectedClasses.map(it => it.id),
        userIds: props.selectedStudents.map(it => it.id)
      });
      modalManager.onDismiss();
      setTimeout(submitActions.disable, 200);
    } catch (err) {
      submitActions.disable();
      console.error(err);
    }
  };
  const url = new URL(`${basePath}${courseDescriptor?.path ?? ""}`, window.location.href);
  if (revisionId !== undefined) {
    url.searchParams.set("revisionId", revisionId ?? "");
    url.searchParams.set("revisionTitle", props.revision.name);
  } else {
    url.searchParams.set("revisionId", "");
    url.searchParams.set("revisionTitle", "");
  }
  return /*#__PURE__*/jsxs("div", {
    children: [/*#__PURE__*/jsxs("h1", {
      className: heading,
      children: ["Del \u2018", props.revision.name, "\u2019"]
    }), /*#__PURE__*/jsx(CopyLinkButton, {
      href: url.toString(),
      buttonIcon: revisionId !== undefined ? /*#__PURE__*/jsx(LinkIcon, {
        width: "21"
      }) : /*#__PURE__*/jsx("span", {
        className: copyLinkThrobber
      }),
      buttonLabel: "Kopi\xE9r link",
      isLoading: revisionId === undefined
    }), /*#__PURE__*/jsx("p", {
      className: shareText,
      children: "Dine elever kan altid tilg\xE5 linket. De kan dog f\xF8rst se det i dropdownen i venstremenuen, hvis du ogs\xE5 har delt det via delingsfunktionen herunder."
    }), /*#__PURE__*/jsxs("div", {
      children: [/*#__PURE__*/jsx(Trigger, {
        label: `Klasse ${loadingDataRef.current ? "(Indlæser...)" : ""}`,
        value: classTriggerValue,
        navigateTo: "/classes",
        disabled: !classes.data,
        "aria-label": "Vælg klasse"
      }), /*#__PURE__*/jsx(Tooltip, {
        body: "V\xE6lg klasse f\xF8rst",
        ariaType: "description",
        placement: "right",
        disabled: props.selectedClasses.length > 0,
        children: ref => /*#__PURE__*/jsx(Trigger, {
          ref: ref,
          label: "Elever",
          value: studentsTriggerValue,
          navigateTo: "/students",
          disabled: studentsDisabled,
          "aria-label": "V\xE6lg elever"
        })
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: buttonsWrapper,
      children: [/*#__PURE__*/jsx(Button, {
        theme: "secondary",
        onClick: () => modalManager.onDismiss(),
        disabled: isSubmitting,
        children: "Annuller"
      }), /*#__PURE__*/jsx(Button, {
        theme: "primary",
        icon: /*#__PURE__*/jsx(ShareIcon, {}),
        disabled: !canShare || isSubmitting,
        onClick: handleShare,
        showSpinner: isSubmitting,
        tooltip: {
          disabled: canShare,
          body: "Hov. Du mangler at vælge hvem du vil dele versionen med",
          ariaType: "description"
        },
        children: "Del version"
      })]
    })]
  });
}

/**
 * utility to read the current revision ID dynamically after opening the share
 * modal (this is necessary, so that we can optimistically open the dialog
 * prior to having fetched ID for new courses)
 */
function useRevisionId(props) {
  const queryClient = useQueryClient();
  const user = useUserIdentityState(state => state.user);
  const [revisionId, setRevisionId] = useState(() => props.revision.__syncedWithBackend ? props.revision.id : undefined);
  useEffect(() => {
    if (props.revision.__syncedWithBackend) {
      return;
    }
    const state = {
      isActive: true
    };
    async function getRevisionId() {
      // In case we've been passed a local revision that has not yet been
      // synced with the backend, then poll our data and wait for the revision
      // to have been successfully saved.
      while (state.isActive) {
        const currentRevisions = queryClient.getQueryData(getAllRevisionsQuery.queryKey({
          user,
          courseId: props.revision.courseId
        }));
        const targetRevision = currentRevisions?.find(it => it.__clientId === props.revision.__clientId);
        if (targetRevision?.__syncedWithBackend) {
          setRevisionId(targetRevision.id);
          break;
        }
        await asyncTimeout(100);
      }
    }
    getRevisionId();
    return () => {
      state.isActive = false;
    };
  }, [props.revision, queryClient, user]);
  return revisionId;
}

export { Root };
//# sourceMappingURL=page.root.js.map
